import { Locale } from "@repo/i18n"
import {
	ALL_BUYER_DELIVERY_COUNTRIES,
	ALL_SUPPORTED_MARKETPLACE_COUNTRIES,
	type MarketplaceRegion,
	PROMOTION_KEY_REGEX,
	PROMOTION_NAME_LOCALES,
	type PromotionNameLocale,
	type PromotionPublicNames,
	STANDARD_SELLER_FEE_BPS,
	SUPPORTED_MARKETPLACE_COUNTRIES,
} from "@repo/shared-primitives"
import { format } from "date-fns"
import {
	fromLocalInputValue,
	toLocalInputValue,
} from "@/components/contests/contest-dates-form"
import type { PromotionStatus } from "@/types/promotions.types"

const BPS_PER_PERCENT = 100
const MAX_KEY_LENGTH = 63

export const MARKETPLACE_REGIONS: readonly MarketplaceRegion[] = ["EU", "NA"]

export const REGION_LABELS: Record<MarketplaceRegion, string> = {
	EU: "Europe",
	NA: "North America",
}

/** Every seller campaign and buyer promotion status, in the order filters list them. */
export const PROMOTION_STATUSES: readonly PromotionStatus[] = [
	"active",
	"scheduled",
	"paused",
	"ended",
]

/** Status names shown in badges, filters and exports. */
export const PROMOTION_STATUS_LABELS: Record<PromotionStatus, string> = {
	active: "Running",
	scheduled: "Scheduled",
	paused: "Paused",
	ended: "Ended",
}

/** Whether `value` is a promotion status. */
export function isPromotionStatus(value: string): value is PromotionStatus {
	return (PROMOTION_STATUSES as readonly string[]).includes(value)
}

/** Formats basis points as a percentage: `0%`, `2.5%`, `8%`. */
export function formatBps(bps: number): string {
	return `${Number((bps / BPS_PER_PERCENT).toFixed(2))}%`
}

/** Converts a percentage typed in a form to whole basis points. */
export function percentToBps(percent: number): number {
	return Math.round(percent * BPS_PER_PERCENT)
}

/** Converts basis points to the percentage shown in a form. */
export function bpsToPercent(bps: number): number {
	return bps / BPS_PER_PERCENT
}

/** Whether `percent` lies in 0..100 (or `min`..100) with at most two decimals. */
export function isValidPercent(percent: number, min = 0): boolean {
	if (!Number.isFinite(percent) || percent < min || percent > 100) {
		return false
	}

	const bps = percent * BPS_PER_PERCENT
	return Math.abs(bps - Math.round(bps)) < 1e-6
}

/** The standard seller fee per region, e.g. "5% in Europe, 8% in North America". */
export function describeStandardSellerFees(): string {
	return MARKETPLACE_REGIONS.map(
		(region) =>
			`${formatBps(STANDARD_SELLER_FEE_BPS[region])} in ${REGION_LABELS[region]}`,
	).join(", ")
}

/**
 * Derives a promotion key from its name: lowercase ASCII letters and digits
 * separated by single dashes, at most 63 characters.
 */
export function slugifyPromotionKey(name: string): string {
	return name
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+/, "")
		.slice(0, MAX_KEY_LENGTH)
		.replace(/-+$/, "")
}

/** Whether `key` is a valid promotion key (2 to 63 lowercase letters, digits or dashes). */
export function isValidPromotionKey(key: string): boolean {
	return PROMOTION_KEY_REGEX.test(key)
}

const countryNames =
	typeof Intl !== "undefined" && "DisplayNames" in Intl
		? new Intl.DisplayNames(["en"], { type: "region" })
		: null

/** English name of an ISO alpha-2 country, or the code when unknown. */
export function countryName(code: string): string {
	try {
		return countryNames?.of(code) ?? code
	} catch {
		return code
	}
}

interface CountryOption {
	value: string
	label: string
}

interface CountryOptionGroup {
	heading: string
	options: CountryOption[]
}

function countryOption(code: string): CountryOption {
	return { value: code, label: `${countryName(code)} (${code})` }
}

/** Seller country options grouped by region, for a `MultiSelect`. */
export function countryOptionGroups(): CountryOptionGroup[] {
	return MARKETPLACE_REGIONS.map((region) => ({
		heading: REGION_LABELS[region],
		options: SUPPORTED_MARKETPLACE_COUNTRIES[region].map(countryOption),
	}))
}

/**
 * Delivery country options for a `MultiSelect`: the seller countries grouped
 * by region, then every other delivery country under "Other delivery
 * countries" when there is any.
 */
export function deliveryCountryOptionGroups(): CountryOptionGroup[] {
	const sellerCountries: readonly string[] = ALL_SUPPORTED_MARKETPLACE_COUNTRIES
	const others = ALL_BUYER_DELIVERY_COUNTRIES.filter(
		(code) => !sellerCountries.includes(code),
	)
	if (others.length === 0) {
		return countryOptionGroups()
	}

	return [
		...countryOptionGroups(),
		{ heading: "Other delivery countries", options: others.map(countryOption) },
	]
}

/** Countries of a region, for "Add all" shortcuts. */
export function regionCountries(region: MarketplaceRegion): readonly string[] {
	return SUPPORTED_MARKETPLACE_COUNTRIES[region]
}

/** "Oct 3, 2026 14:00", in the browser's time zone. */
export function formatDateTime(iso: string): string {
	return format(new Date(iso), "MMM d, yyyy HH:mm")
}

/** "Oct 3, 2026", in the browser's time zone. */
export function formatDay(iso: string): string {
	return format(new Date(iso), "MMM d, yyyy")
}

/** "Oct 3, 2026 → Dec 31, 2026", with "no end date" for an open window. */
export function formatWindow(startsAt: string, endsAt: string | null): string {
	return `${formatDay(startsAt)} → ${endsAt ? formatDay(endsAt) : "no end date"}`
}

/** Formats an amount in minor units, e.g. 2000 EUR → "€20.00". */
export function formatCents(cents: number, currency: string): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency.toUpperCase(),
	}).format(cents / 100)
}

/** The datetime-local value showing an ISO instant, empty when unset. */
export function isoToLocalInput(iso: string | null | undefined): string {
	return toLocalInputValue(iso)
}

/**
 * Converts a datetime-local value to an ISO instant. When the field still
 * shows `initialIso`, that instant is returned unchanged, because the picker
 * drops seconds. Returns null for an empty or invalid value.
 */
export function localInputToIso(
	value: string,
	initialIso?: string | null,
): string | null {
	if (initialIso && value === toLocalInputValue(initialIso)) {
		return initialIso
	}

	const date = fromLocalInputValue(value)
	return date ? date.toISOString() : null
}

/** Language names shown next to promotion public names. */
export const LANGUAGE_NAMES: Record<Locale, string> = {
	[Locale.English]: "English",
	[Locale.French]: "French",
	[Locale.German]: "German",
	[Locale.Italian]: "Italian",
	[Locale.Spanish]: "Spanish",
}

/** Public-name languages besides English; each one falls back to the English name. */
export const TRANSLATED_NAME_LOCALES: readonly PromotionNameLocale[] =
	PROMOTION_NAME_LOCALES.filter((locale) => locale !== Locale.English)

/** A promotion's public name per language as typed in a form, empty while blank. */
export type PromotionNameFormValues = Record<PromotionNameLocale, string>

/** Public names written so far; any language but English may be missing. */
export type PromotionNamesByLocale = Partial<
	Record<PromotionNameLocale, string>
>

/** Form values for a promotion's public names: the saved ones, blank otherwise. */
export function promotionNameFormDefaults(
	names?: PromotionPublicNames | null,
): PromotionNameFormValues {
	return Object.fromEntries(
		PROMOTION_NAME_LOCALES.map((locale) => [locale, names?.[locale] ?? ""]),
	) as PromotionNameFormValues
}

/** What to fix in a promotion's public names; empty when they can be saved. */
export function promotionNameIssues(values: PromotionNameFormValues): string[] {
	if (values[Locale.English].trim().length === 0) {
		return ["Give the promotion an English public name."]
	}

	return []
}

/**
 * Public names to save from form values: English trimmed, every other
 * language only when it is not blank.
 */
export function toPromotionPublicNames(
	values: PromotionNameFormValues,
): PromotionPublicNames {
	const names: PromotionPublicNames = {
		[Locale.English]: values[Locale.English].trim(),
	}

	for (const locale of TRANSLATED_NAME_LOCALES) {
		const name = values[locale].trim()
		if (name) {
			names[locale] = name
		}
	}

	return names
}

/**
 * The name shown in `locale`, e.g. "In French: Frais de service offerts", or
 * "In French: uses the English name" when none was written.
 */
export function describeTranslatedName(
	names: PromotionNamesByLocale,
	locale: PromotionNameLocale,
): string {
	const name = names[locale]?.trim()

	return `In ${LANGUAGE_NAMES[locale]}: ${name || "uses the English name"}`
}
